/* eslint-disable lingui/no-unlocalized-strings */
import { useSignIn } from '@clerk/clerk-react';
import { useMutation } from '@tanstack/react-query';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { ClerkError } from '../../ifaces/clerk';
import { InviteJsonResponse } from '../../ifaces/user';
import { TutorAvatar } from '../components/TutorAvatar';
import { postJson } from '../util/xhr';
import { Text } from './../components/Typography';

export function SectionLoginPage() {
  const navigate = useNavigate();
  const { signIn } = useSignIn();
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [email, setEmail] = useState('');

  const { mutate: submitSection, isPending: isUpdating } = useMutation({
    mutationFn: async () =>
      sectionJson({
        email,
      }),
    onSuccess: async (resp: InviteJsonResponse) => {
      if (resp.status && resp.status === 200 && resp.body?.email) {
        // @ts-expect-error Mixing build styles in the same codebase
        const redirectUrlEmail = import.meta.env.VITE_HOST_REDIRECT;

        try {
          await signIn!.create({
            strategy: 'email_link',
            identifier: email,
            redirectUrl: redirectUrlEmail,
          });
        } catch (err: unknown) {
          if (err && typeof err === 'object' && 'clerkError' in err && err.clerkError) {
            const clerkErr = err as ClerkError;
            // if there's a SAML error, redirect to corp-login after a few seconds
            if (
              clerkErr.errors.find(
                (e) =>
                  e.code === 'form_conditional_param_value_disallowed' &&
                  /SAML/.test(e.longMessage!),
              )
            ) {
              setError(
                `Please use the corporate login page with this address. Redirecting you now...`,
              );
              setTimeout(() => {
                navigate('/corp-login');
              }, 3000);

              return;
            }
          }

          setError(`There was an error with your account login. Please retry or contact support.`);
          return;
        }

        setSuccess(`Section Account Confirmed! Please Check Your Email For A Login Link.`);
      } else {
        let errorMsg = `There was an error with your account login. Please retry or contact support.`;
        if (resp.body?.text) {
          errorMsg = resp.body.text;
        }
        setError(errorMsg);
      }
    },
    onError: (err) => {
      setError(
        err instanceof Error
          ? err.message
          : `There was an error with your account login. Please retry or contact support.`,
      );
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setSuccess(null);
    setError(null);
    submitSection();
  };

  const handleEmailUpdate = (e: React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    const emailInput = e.target.value.toLocaleLowerCase();
    setEmail(emailInput);
  };

  return (
    <div className="login-page">
      <div className="login-box-container">
        <div className="half-login-page half-login-left">
          <img
            alt={`ProfAI Login`}
            src="/img/loginAbstract.png"
            width={'100%'}
            style={{ borderRadius: '10px 0 0 10px', float: 'left' }}
          />
        </div>
        <div className="half-login-page">
          <div className="invite-form">
            <div className="invite-avatar-holder">
              <TutorAvatar size={80} />
            </div>

            <Text header l bold style={{ marginBottom: '10px' }}>
              <span className="invite-title-text" style={{ color: 'var(--colors-tomato)' }}>
                {' '}
                Welcome to ProfAI
              </span>
            </Text>

            <Text header xs bold>
              <span className="invite-sub-text">
                To login you&apos;ll need a Section account or access from your employer.
              </span>
            </Text>

            <div className="invite-result-text">
              {isUpdating && <div className="loading-message">Loading...</div>}
              {error && <div className="error-message">{error}</div>}
              {success && <div className="success-message">{success}</div>}
            </div>

            <div className="invite-form-left-panel">
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label htmlFor="email">Section Account Email:</label>
                  <input
                    id="email"
                    type="text"
                    value={email}
                    onChange={(e) => handleEmailUpdate(e)}
                    className="app-input form-input"
                    required
                  />
                </div>
                <div className="form-actions" style={{ margin: '40px 0 0px' }}>
                  <button type="submit" disabled={isUpdating} className="save-button neo large">
                    {isUpdating ? `Checking Section Account...` : `Login`}
                  </button>
                </div>
                <div className="bottom-url">
                  <button className="login-text-btn" onClick={() => navigate('/corp-login')}>
                    Did your company give you access to ProfAI? Click here
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

async function sectionJson(body = {}): Promise<InviteJsonResponse> {
  const response = await postJson(`api/user/email`, body, false);
  const json = await response.json();
  return {
    status: response.status,
    body: json,
  };
}
